import { motion } from 'framer-motion';
import { Check, Timer, X } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  BottomSheet,
  Button,
  NumPad,
  useToast,
} from '../../../components';
import { selectExpenseCategories, useActions, useAppData } from '../../../data';
import { cn } from '../../../lib/cn';
import { formatCurrency } from '../../../lib/currency';
import { toIsoDate } from '../../../lib/date';
import { springGentle } from '../../../tokens';

const STORAGE_KEY = 'margin:impulse-timer';
const DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

type StoredTimer = {
  name: string;
  cost: number;
  startedAt: number; // epoch ms
};

function readTimer(): StoredTimer | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredTimer;
    if (
      typeof parsed.name === 'string' &&
      typeof parsed.cost === 'number' &&
      typeof parsed.startedAt === 'number'
    ) {
      return parsed;
    }
  } catch {
    /* fall through */
  }
  return null;
}

function writeTimer(t: StoredTimer | null) {
  if (typeof localStorage === 'undefined') return;
  if (!t) localStorage.removeItem(STORAGE_KEY);
  else localStorage.setItem(STORAGE_KEY, JSON.stringify(t));
}

function formatRemaining(ms: number): string {
  if (ms <= 0) return '00:00:00';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
}

type Props = { open: boolean; onClose: () => void };

export function ImpulseTimer({ open, onClose }: Props) {
  const data = useAppData();
  const actions = useActions();
  const toast = useToast();
  const currency = data.profile.currency;

  const [timer, setTimer] = useState<StoredTimer | null>(() => readTimer());
  const [name, setName] = useState('');
  const [cost, setCost] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  // Re-read on open in case another instance stashed one.
  useEffect(() => {
    if (open) setTimer(readTimer());
  }, [open]);

  // Tick every second while open and a timer is running.
  useEffect(() => {
    if (!open || !timer) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [open, timer]);

  const expense = useMemo(() => selectExpenseCategories(data), [data]);

  if (!timer) {
    return (
      <BottomSheet open={open} onClose={onClose} title="Impulse timer">
        <div className="px-card-pad py-4 flex flex-col gap-3">
          <p className="text-[14px] text-text-secondary text-center">
            Tempted by something? Park it here for 24 hours. If you still
            want it tomorrow, add it as a transaction. If not, you saved
            yourself the spend.
          </p>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="What is it?"
            autoCapitalize="sentences"
            maxLength={60}
            className="w-full h-12 px-4 rounded-button bg-surface-secondary text-[16px] text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-accent/40"
          />
        </div>
        <NumPad
          initialValue={cost}
          onChange={setCost}
          onDone={(committed) => {
            if (!name.trim()) {
              toast.show({ message: 'Give it a name first', kind: 'warning' });
              return;
            }
            if (committed <= 0) return;
            const next: StoredTimer = {
              name: name.trim(),
              cost: committed,
              startedAt: Date.now(),
            };
            writeTimer(next);
            setTimer(next);
            setName('');
            setCost(0);
            toast.show({
              message: 'Timer started. Check back in 24 hours',
              kind: 'success',
            });
          }}
          doneLabel="Start 24-hour timer"
          caption={null}
        />
      </BottomSheet>
    );
  }

  const elapsed = now - timer.startedAt;
  const remaining = Math.max(0, DURATION_MS - elapsed);
  const expired = remaining === 0;
  const progress = Math.min(1, elapsed / DURATION_MS);

  const dismiss = (kind: 'kept' | 'skipped') => {
    writeTimer(null);
    setTimer(null);
    if (kind === 'kept') {
      const categoryId =
        expense.find((c) => c.icon === 'Sparkles')?.id ?? expense[0]?.id;
      if (categoryId) {
        actions.addTransaction({
          amount: timer.cost,
          type: 'expense',
          categoryId,
          description: timer.name,
          date: toIsoDate(new Date()),
          source: 'manual',
        });
        toast.show({
          message: `Added ${formatCurrency(timer.cost, { currency })}`,
          kind: 'success',
        });
      }
    } else {
      toast.show({
        message: 'Saved yourself the impulse spend',
        kind: 'success',
      });
    }
    onClose();
  };

  return (
    <BottomSheet open={open} onClose={onClose} title="Impulse timer">
      <div className="px-card-pad py-4 flex flex-col items-center gap-4">
        <div className="text-center">
          <div className="text-[15px] font-semibold text-text-primary">
            {timer.name}
          </div>
          <div className="mt-1 text-[14px] font-mono tabular-nums text-text-secondary">
            {formatCurrency(timer.cost, { currency })}
          </div>
        </div>

        <CountdownRing progress={progress} expired={expired} size={172}>
          <div className="text-center">
            <div
              className={cn(
                'text-[26px] font-light font-mono tabular-nums',
                expired ? 'text-positive' : 'text-text-primary',
              )}
            >
              {expired ? '24h' : formatRemaining(remaining)}
            </div>
            <div className="text-[11px] uppercase tracking-[0.05em] text-text-tertiary mt-1">
              {expired ? 'Time’s up' : 'remaining'}
            </div>
          </div>
        </CountdownRing>

        {expired ? (
          <div className="w-full flex flex-col gap-2">
            <p className="text-center text-[15px] font-medium text-text-primary">
              Still want it?
            </p>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="secondary"
                fullWidth
                size="lg"
                leadingIcon={<X size={16} strokeWidth={1.5} />}
                onClick={() => {
                  // Unlock the achievement only when the user *waited
                  // out* the full 24h and chose to pass — cancelling the
                  // timer early (the ghost button below) doesn't count.
                  actions.unlockAchievement('impulse-resist');
                  dismiss('skipped');
                }}
              >
                Skip it
              </Button>
              <Button
                variant="primary"
                fullWidth
                size="lg"
                leadingIcon={<Check size={16} strokeWidth={1.5} />}
                onClick={() => dismiss('kept')}
              >
                Add it
              </Button>
            </div>
          </div>
        ) : (
          <div className="w-full flex flex-col gap-2">
            <p className="text-center text-[13px] text-text-secondary">
              Come back when the timer ends and decide.
            </p>
            <Button
              variant="ghost"
              fullWidth
              leadingIcon={<Timer size={16} strokeWidth={1.5} />}
              onClick={() => dismiss('skipped')}
            >
              Cancel timer
            </Button>
          </div>
        )}
      </div>
    </BottomSheet>
  );
}

function CountdownRing({
  progress,
  expired,
  size,
  children,
}: {
  progress: number;
  expired: boolean;
  size: number;
  children: ReactNode;
}) {
  const stroke = 10;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - Math.max(0, Math.min(1, progress)));
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          className="text-surface-secondary"
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          strokeLinecap="round"
          className={cn(expired ? 'text-positive' : 'text-accent')}
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={springGentle}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        {children}
      </div>
    </div>
  );
}
